// Parity P1 wave 1: a task as a full page (`/c/{slug}/work/t/{id}`; hybrid design §3.2 "renders
// the same component in the main region (work ≤ 960, props, rail fixed 400). Esc returns to
// where it was opened"). The view is `TaskView` in its `page` host; its panes follow the main
// region's width like the drawer's follow the drawer's. Another task opened from it (a subtask,
// a dependency) replaces it here.
import { PageFrame } from "@companyos/ui";
import { createRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useCompany } from "../features/use-feature";
import { TaskView } from "../features/work/task-page/task-view";
import { keepFocus } from "../focus/focus";
import { workRoute } from "./c.$slug.work";

export const workTaskPageRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "t/$taskId",
  component: function WorkTaskPage() {
    const { api } = workTaskPageRoute.useRouteContext();
    const { taskId } = workTaskPageRoute.useParams();
    const company = useCompany();
    const navigate = useNavigate();
    const router = useRouter();
    const back = () => {
      if (window.history.length > 1) router.history.back();
      else
        void navigate({
          to: "/c/$slug/work",
          params: { slug: company.slug },
          search: keepFocus,
        } as never);
    };
    // Esc: back to where it was opened (unless something inside handled it: a menu, a dialog).
    // biome-ignore lint/correctness/useExhaustiveDependencies: `back` reads the router at call time.
    useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        if (e.key !== "Escape" || e.defaultPrevented) return;
        if (document.querySelector("[role=dialog], [role=menu], [data-slot=popover-content]"))
          return;
        back();
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, []);
    return (
      <PageFrame
        width="full"
        className="flex h-full min-h-full flex-col bg-card"
        data-screen="task-page"
      >
        <TaskView
          key={taskId}
          api={api}
          company={company}
          taskId={taskId}
          host="page"
          onClose={back}
          onOpenTask={(id) =>
            void navigate({
              to: "/c/$slug/work/t/$taskId",
              params: { slug: company.slug, taskId: id },
              search: keepFocus,
            } as never)
          }
        />
      </PageFrame>
    );
  },
});
